import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { Loader } from "@/components/ui-helpers";
import { CheckSquare } from "lucide-react";
import { prettyDate } from "@/mobile/mobileUtils";
import { toast } from "sonner";

const STATUSES = ["To Do", "In Progress", "Waiting for Client", "Completed", "Cancelled"];
const STATUS_STYLE = {
  "To Do": "bg-slate-100 text-slate-600",
  "In Progress": "bg-blue-100 text-blue-700",
  "Waiting for Client": "bg-amber-100 text-amber-700",
  "Completed": "bg-emerald-100 text-emerald-700",
  "Cancelled": "bg-rose-100 text-rose-600",
};

export default function MobileTasks() {
  const [tasks, setTasks] = useState(null);
  const [filter, setFilter] = useState("open");

  const load = () => api.get("/tasks").then((r) => setTasks(r.data || [])).catch(() => setTasks([]));
  useEffect(() => { load(); }, []);

  const setStatus = async (t, status) => {
    setTasks((prev) => prev.map((x) => (x.id === t.id ? { ...x, status } : x)));
    try { await api.put(`/tasks/${t.id}`, { status }); toast.success("Task updated"); }
    catch (e) { toast.error(apiErr(e)); load(); }
  };

  if (!tasks) return <Loader />;
  const shown = tasks.filter((t) => filter === "all" ? true : !["Completed", "Cancelled"].includes(t.status));

  return (
    <div className="p-4 space-y-4" data-testid="mobile-tasks">
      <div className="flex items-center justify-between">
        <h1 className="font-head font-extrabold text-xl text-slate-900">Tasks</h1>
        <div className="flex bg-slate-100 rounded-xl p-0.5 text-xs font-semibold">
          {["open", "all"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`tasks-filter-${f}`} className={`px-3 py-1.5 rounded-lg capitalize ${filter === f ? "bg-white shadow-sm text-primary" : "text-slate-500"}`}>{f}</button>
          ))}
        </div>
      </div>

      {shown.length === 0 && <div className="text-center py-12 text-slate-400"><CheckSquare className="h-8 w-8 mx-auto mb-2" /><div className="text-sm">No {filter === "open" ? "open " : ""}tasks.</div></div>}

      <div className="space-y-2.5">
        {shown.map((t) => (
          <div key={t.id} className="bg-white rounded-2xl p-4 border border-slate-100" data-testid={`task-card-${t.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="font-semibold text-slate-800 text-sm">{t.title}</div>
              {t.priority && <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${t.priority === "High" ? "bg-rose-100 text-rose-600" : "bg-slate-100 text-slate-500"}`}>{t.priority}</span>}
            </div>
            {t.description && <div className="text-xs text-slate-500 mt-1">{t.description}</div>}
            <div className="text-[11px] text-slate-400 mt-1.5">
              {t.client_name && <span>{t.client_name}{t.app_number ? ` · ${t.app_number}` : ""}</span>}
              {t.due_date && <span> · Due {prettyDate(t.due_date)}</span>}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLE[t.status] || "bg-slate-100 text-slate-600"}`}>{t.status}</span>
              <select value={t.status} onChange={(e) => setStatus(t, e.target.value)} data-testid={`task-status-${t.id}`}
                className="ml-auto text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
